/**
 * Canonical donation/auth error-code catalog (plan §8).
 *
 * These string codes are emitted by the backend in `{ code: string }` and
 * MUST be mapped consistently to user-facing copy across both the customer
 * PWA and the kiosk. Do NOT introduce app-local aliases; if a new code is
 * needed, add it here and to the backend's `ErrorCodes` enum.
 */
import { isCommerceBlockedErrorCode } from 'pi-kiosk-shared/eet/CommerceBlockedErrorCode';
import i18n from '../../i18n.js';

export const DonationErrorCodes = {
  /** Whichever fallback not configured or inactive (HTTP 409). */
  DONATION_WHICHEVER_TARGET_NOT_CONFIGURED:
    'DONATION_WHICHEVER_TARGET_NOT_CONFIGURED',
  /** Duplicate/terminal session payment start attempt (HTTP 409). */
  CHECKOUT_SESSION_INVALID_STATE: 'CHECKOUT_SESSION_INVALID_STATE',
  /** Session not found in tenant scope (HTTP 404). */
  CHECKOUT_SESSION_NOT_FOUND: 'CHECKOUT_SESSION_NOT_FOUND',
  /** Provider/config unavailable for donation payment (HTTP 422). */
  DONATION_PAYMENT_CONFIGURATION_INVALID:
    'DONATION_PAYMENT_CONFIGURATION_INVALID',
  /** Legacy donation 410 (no longer thrown for in-scope payable journeys). */
  DONATION_CHECKOUT_PAYMENT_NOT_AVAILABLE:
    'DONATION_CHECKOUT_PAYMENT_NOT_AVAILABLE',
  /** Generic OTP/auth codes used by the donation FE for unified messaging. */
  OTP_EXPIRED: 'OTP_EXPIRED',
  OTP_INVALID: 'OTP_INVALID',
  /** Backend validation — e.g. customer e-mail missing when starting payment (HTTP 422). */
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
  BANK_ACCOUNT_PURPOSE_NOT_ALLOWED: 'BANK_ACCOUNT_PURPOSE_NOT_ALLOWED',
  BANK_ACCOUNT_NOT_CONFIGURED: 'BANK_ACCOUNT_NOT_CONFIGURED',
  BANK_ACCOUNT_INACTIVE: 'BANK_ACCOUNT_INACTIVE',
  DUPLICATE_BANK_REFERENCE: 'DUPLICATE_BANK_REFERENCE',
  PAYMENT_CLAIM_CAP_REACHED: 'PAYMENT_CLAIM_CAP_REACHED',
  PAYMENT_CLAIM_COOLDOWN: 'PAYMENT_CLAIM_COOLDOWN',
  PAYMENT_CLAIM_DISABLED: 'PAYMENT_CLAIM_DISABLED',
} as const;

export type DonationErrorCode =
  (typeof DonationErrorCodes)[keyof typeof DonationErrorCodes];

/**
 * Returns the canonical user-facing message for a donation error code.
 * Falls back to a generic message when the code is unknown.
 */
export function donationErrorMessage(
  code: string | undefined | null,
  fallback?: string
): string {
  const genericFallback =
    fallback ?? i18n.t('errors.generic', { ns: 'customer' });

  // HYG-G15 / wave4-surface-eet-ux — never surface COMMERCE_BLOCKED / EET / EIČ / POK.
  if (isCommerceBlockedErrorCode(code)) {
    return i18n.t('shop.payDisabled.paymentsUnavailable', { ns: 'customer' });
  }

  switch (code) {
    case DonationErrorCodes.DONATION_WHICHEVER_TARGET_NOT_CONFIGURED:
      return i18n.t('errors.donationWhicheverNotConfigured', { ns: 'customer' });
    case DonationErrorCodes.CHECKOUT_SESSION_INVALID_STATE:
      return i18n.t('errors.checkoutSessionInvalidState', { ns: 'customer' });
    case 'CHECKOUT_MODE_REQUIRED_RECREATE_SESSION':
      return i18n.t('errors.checkoutModeRecreateRequired', { ns: 'customer' });
    case DonationErrorCodes.CHECKOUT_SESSION_NOT_FOUND:
      return i18n.t('errors.checkoutSessionNotFound', { ns: 'customer' });
    case DonationErrorCodes.DONATION_PAYMENT_CONFIGURATION_INVALID:
      return i18n.t('errors.donationPaymentConfigInvalid', { ns: 'customer' });
    case DonationErrorCodes.DONATION_CHECKOUT_PAYMENT_NOT_AVAILABLE:
      return i18n.t('errors.donationCheckoutNotAvailable', { ns: 'customer' });
    case DonationErrorCodes.OTP_EXPIRED:
      return i18n.t('errors.otpExpired', { ns: 'customer' });
    case DonationErrorCodes.OTP_INVALID:
      return i18n.t('errors.otpInvalid', { ns: 'customer' });
    case DonationErrorCodes.MISSING_REQUIRED_FIELD:
      return i18n.t('errors.missingRequiredField', { ns: 'customer' });
    case 'COLLECT_METADATA_REQUIRED':
      return i18n.t('errors.collectMetadataRequired', { ns: 'customer' });
    case 'SCHEDULED_PICKUP_NOT_ENABLED':
      return i18n.t('errors.scheduledPickupNotEnabled', { ns: 'customer' });
    case 'HANDOFF_SESSION_EXPIRED':
      return i18n.t('errors.handoffSessionExpired', { ns: 'customer' });
    case 'HANDOFF_SESSION_NOT_FOUND':
      return i18n.t('errors.handoffSessionNotFound', { ns: 'customer' });
    case 'CHECKOUT_JOURNEY_INVALID':
      return i18n.t('errors.checkoutJourneyInvalid', { ns: 'customer' });
    case 'BUY_AGAIN_NO_STOCK':
      return i18n.t('errors.buyAgainNoStock', { ns: 'customer' });
    case 'BUY_AGAIN_PM_INACTIVE':
      return i18n.t('errors.buyAgainPmInactive', { ns: 'customer' });
    case 'PLATFORM_CHROME_NO_API_TENANT':
    case 'OPEN_SHOP_TO_CONTINUE':
      return i18n.t('errors.openShopToContinue', { ns: 'customer' });
    case DonationErrorCodes.BANK_ACCOUNT_PURPOSE_NOT_ALLOWED:
    case DonationErrorCodes.BANK_ACCOUNT_NOT_CONFIGURED:
    case DonationErrorCodes.BANK_ACCOUNT_INACTIVE:
    case DonationErrorCodes.DUPLICATE_BANK_REFERENCE:
    case DonationErrorCodes.PAYMENT_CLAIM_CAP_REACHED:
    case DonationErrorCodes.PAYMENT_CLAIM_COOLDOWN:
    case 'PROVIDER_UNAVAILABLE':
      return i18n.t('errors.paymentStartFailed', { ns: 'customer' });
    case DonationErrorCodes.PAYMENT_CLAIM_DISABLED:
      return i18n.t('errors.paymentClaimDisabled', { ns: 'customer' });
    default:
      return genericFallback;
  }
}
